import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Popper, 
  Paper, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Box,
  ClickAwayListener,
  styled,
  useTheme,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import { getUsers } from '../../../services/userService';
import SecureAvatar from '../SecureAvatar';
import CloseIcon from '@mui/icons-material/Close';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';

// Custom styles for the input container
const MentionInputContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

// Custom styles for the editable div that holds text and chips
const EditableDiv = styled('div')(({ theme }) => ({
  minHeight: '100px',
  maxHeight: '200px',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body1.fontSize,
  lineHeight: '1.4375em',
  overflow: 'auto',
  width: '100%',
  whiteSpace: 'pre-wrap', // Preserve whitespace and line breaks
  wordBreak: 'break-word',
  wordWrap: 'break-word',
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
  },
  '&:hover': {
    borderColor: theme.palette.text.primary,
  },
  // Add list styling
  '& ul, & ol': {
    margin: '8px 0',
    paddingLeft: '24px',
  },
  '& ul': {
    listStyleType: 'disc',
  },
  '& ol': {
    listStyleType: 'decimal',
  },
  '& li': {
    marginBottom: '4px',
    lineHeight: '1.5',
  },
  '& ul ul': {
    listStyleType: 'circle',
  },
  '& ul ul ul': {
    listStyleType: 'square',
  },
  '& .mention-chip': {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    backgroundColor: theme.palette.mode === 'light' 
      ? theme.palette.primary.main 
      : theme.palette.primary.light,
    color: theme.palette.mode === 'light'
      ? theme.palette.primary.contrastText
      : 'white',
    borderRadius: '16px',
    padding: '2px 2px 2px 4px',
    margin: '0 2px',
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    position: 'relative',
    gap: '4px',
  },
  '& .mention-avatar': {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: theme.palette.mode === 'light'
      ? theme.palette.primary.dark
      : theme.palette.primary.dark,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    color: theme.palette.mode === 'light'
      ? theme.palette.primary.contrastText
      : 'white',
    flexShrink: 0,
  },
  '& .mention-text': {
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: '4px',
  },
  '& .mention-remove': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    marginLeft: '2px',
    borderRadius: '50%',
    backgroundColor: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.4)'
      : 'rgba(255, 255, 255, 0.3)',
    color: theme.palette.mode === 'light'
      ? theme.palette.primary.contrastText
      : 'white',
    cursor: 'pointer',
    fontSize: '12px',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'light'
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(255, 255, 255, 0.5)',
    }
  },
  '&:empty:before': {
    content: 'attr(data-placeholder)',
    color: theme.palette.text.disabled,
  }
}));

// Regex constants
const TRIGGER_CHAR = '@';

/**
 * InlineMentionInput component
 * A text input with inline chips for mentions
 */
const InlineMentionInput = React.forwardRef(({ 
  value = '', 
  onChange, 
  onMentionAdd,
  onMentionRemove,
  placeholder = 'Write your comment here...',
  params = {},
  inputProps = {},
}, ref) => {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastSearched, setLastSearched] = useState('');
  
  // Refs - use forwarded ref or create internal ref
  const inputRef = useRef(null);
  const finalRef = ref || inputRef;
  
  // Mention being typed
  const [currentMention, setCurrentMention] = useState({
    mentioning: false,
    startPosition: 0,
    text: '',
    node: null
  });
  
  // Track if we just inserted a mention to prevent immediate reopening
  const [justInsertedMention, setJustInsertedMention] = useState(false);
  
  // Helper function to check if cursor is right after a mention chip
  const isCursorAfterMentionChip = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;
    
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    
    // Check if we're at the beginning of a text node that follows a mention chip
    if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
      const prevSibling = node.previousSibling;
      return prevSibling && prevSibling.classList && prevSibling.classList.contains('mention-chip');
    }
    
    return false;
  };
  
  // Reset input (for clearing after submission)
  const resetInput = () => {
    if (finalRef.current) {
      finalRef.current.innerHTML = '';
    }
  };
  
  // Helper function to set cursor at the end
  const setCursorAtEnd = useCallback(() => {
    if (!finalRef.current) return;
    
    const range = document.createRange();
    const selection = window.getSelection();
    
    // Move to the end of the content
    range.selectNodeContents(finalRef.current);
    range.collapse(false); // false means collapse to end
    
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);
  
  // Helper function to ensure cursor is visible
  const ensureCursorVisible = () => {
    if (!finalRef.current) return;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    try {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = finalRef.current.getBoundingClientRect();
      
      // Scroll if cursor is below visible area
      if (rect.bottom > containerRect.bottom - 10) {
        finalRef.current.scrollTop += (rect.bottom - containerRect.bottom) + 20;
      }
      // Scroll if cursor is above visible area
      else if (rect.top < containerRect.top + 10) {
        finalRef.current.scrollTop -= (containerRect.top - rect.top) + 20;
      }
    } catch (e) {
      // Fallback: just scroll to bottom
      finalRef.current.scrollTop = finalRef.current.scrollHeight;
    }
  };
  
  // Function to convert text mentions to chip elements
  const convertMentionsToChips = async (text) => {
    if (!text) return '';
    
    // Decode HTML entities first
    // Handle double-encoded entities first (e.g., &amp;nbsp; -> &nbsp; -> space)
    let decodedText = text
      .replace(/&amp;nbsp;/g, ' ')  // Handle double-encoded nbsp first
      .replace(/&amp;lt;/g, '<')
      .replace(/&amp;gt;/g, '>')
      .replace(/&amp;quot;/g, '"')
      .replace(/&amp;#39;/g, "'")
      .replace(/&amp;amp;/g, '&');
      
    // Then handle single-encoded entities
    decodedText = decodedText
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
    
    // Create a temporary container to work with
    const container = document.createElement('div');
    
    // Check if the text contains HTML list tags
    if (decodedText.includes('<ul>') || decodedText.includes('<ol>') || decodedText.includes('<li>')) {
      // Process as HTML to preserve list structure
      container.innerHTML = decodedText;
      
      // Process mentions within the HTML
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          const mentionPattern = /@(\w+)_(\w+)/g;
          let lastIndex = 0;
          let match;
          const fragments = [];
          
          while ((match = mentionPattern.exec(text)) !== null) {
            // Add text before mention
            if (match.index > lastIndex) {
              fragments.push(document.createTextNode(text.substring(lastIndex, match.index)));
            }
            
            // Create mention chip
            const firstName = match[1];
            const lastName = match[2];
            const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
            
            const chipElement = document.createElement('span');
            chipElement.className = 'mention-chip';
            chipElement.contentEditable = 'false';
            chipElement.setAttribute('data-mention', match[0]);
            
            const avatarElement = document.createElement('span');
            avatarElement.className = 'mention-avatar';
            avatarElement.textContent = initials;
            
            const textElement = document.createElement('span');
            textElement.className = 'mention-text';
            textElement.textContent = `${firstName} ${lastName}`;
            
            const removeElement = document.createElement('span');
            removeElement.className = 'mention-remove';
            removeElement.innerHTML = '<span class="mention-remove-icon">×</span>';
            
            chipElement.appendChild(avatarElement);
            chipElement.appendChild(textElement);
            chipElement.appendChild(removeElement);
            
            fragments.push(chipElement);
            lastIndex = match.index + match[0].length;
          }
          
          // Add remaining text
          if (lastIndex < text.length) {
            fragments.push(document.createTextNode(text.substring(lastIndex)));
          }
          
          // Replace the text node with fragments
          if (fragments.length > 0) {
            const parent = node.parentNode;
            fragments.forEach(fragment => {
              parent.insertBefore(fragment, node);
            });
            parent.removeChild(node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Recursively process child nodes
          Array.from(node.childNodes).forEach(child => processNode(child));
        }
      };
      
      // Process all nodes in the container
      Array.from(container.childNodes).forEach(node => processNode(node));
      
      return container.innerHTML;
    }
    
    // Original line-by-line processing for non-HTML content
    const lines = decodedText.split('\n');
    
    lines.forEach((line, lineIndex) => {
      // Process mentions in this line
      const mentionPattern = /@(\w+)_(\w+)/g;
      let lastIndex = 0;
      let match;
      
      while ((match = mentionPattern.exec(line)) !== null) {
        // Add text before the mention
        if (match.index > lastIndex) {
          const textBeforeMention = line.substring(lastIndex, match.index);
          // Check if the text contains HTML formatting
          if (/<[^>]+>/.test(textBeforeMention)) {
            // Create a span to hold the formatted text
            const span = document.createElement('span');
            span.innerHTML = textBeforeMention
              // Only allow specific formatting tags and list tags
              .replace(/<(?!\/?(?:b|i|u|s|ul|ol|li)(?:>|\s.*?>))[^>]*>/g, '');
            container.appendChild(span);
          } else {
            const textNode = document.createTextNode(textBeforeMention);
            container.appendChild(textNode);
          }
        }
        
        // Create the mention chip element
        const firstName = match[1];
        const lastName = match[2];
        const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
        
        const chipElement = document.createElement('span');
        chipElement.className = 'mention-chip';
        chipElement.contentEditable = 'false';
        chipElement.setAttribute('data-mention', match[0]);
        
        const avatarElement = document.createElement('span');
        avatarElement.className = 'mention-avatar';
        avatarElement.textContent = initials;
        
        const textElement = document.createElement('span');
        textElement.className = 'mention-text';
        textElement.textContent = `${firstName} ${lastName}`;
        
        const removeElement = document.createElement('span');
        removeElement.className = 'mention-remove';
        removeElement.innerHTML = '<span class="mention-remove-icon">×</span>';
        
        chipElement.appendChild(avatarElement);
        chipElement.appendChild(textElement);
        chipElement.appendChild(removeElement);
        
        container.appendChild(chipElement);
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text after the last mention
      if (lastIndex < line.length) {
        const remainingText = line.substring(lastIndex);
        // Check if the text contains HTML formatting
        if (/<[^>]+>/.test(remainingText)) {
          // Create a span to hold the formatted text
          const span = document.createElement('span');
          span.innerHTML = remainingText
            // Only allow specific formatting tags and list tags
            .replace(/<(?!\/?(?:b|i|u|s|ul|ol|li)(?:>|\s.*?>))[^>]*>/g, '');
          container.appendChild(span);
        } else {
          const textNode = document.createTextNode(remainingText);
          container.appendChild(textNode);
        }
      }
      
      // Add line break if not the last line
      if (lineIndex < lines.length - 1) {
        container.appendChild(document.createElement('br'));
      }
    });
    
    return container.innerHTML;
  };
  
  // Initialize with initial value on mount
  useEffect(() => {
    if (finalRef.current && value && value !== '') {
      // Convert mentions to chips before setting innerHTML
      // This will also decode HTML entities for display
      convertMentionsToChips(value).then(html => {
        if (finalRef.current) {
          finalRef.current.innerHTML = html;
        }
      });
    }
  }, []); // Only run on mount
  
  // Update value when changed externally
  useEffect(() => {
    if (!finalRef.current) return;

    const currentContent = finalRef.current.innerHTML;

    if (value === '' && currentContent !== '') {
      resetInput();
    } else if (value && value !== '') {
      // Check if the current content already has the same text (ignoring HTML)
      const currentText = getTextValueWithMentions();
      // Also decode the incoming value for comparison to handle HTML entities
      const decodedValue = value
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
      if (currentText !== value && currentText !== decodedValue) {
        // Don't update if user is actively focused in this field to prevent cursor jumping
        const isFocused = document.activeElement === finalRef.current;
        if (isFocused) {
          console.log('[InlineMentionInput] Skipping external update - user is typing');
          return;
        }

        // Convert mentions to chips before setting
        convertMentionsToChips(value).then(html => {
          if (finalRef.current) {
            finalRef.current.innerHTML = html;
          }
        });
      }
    }
  }, [value, setCursorAtEnd]);
  
  // Initialize event handlers for mention chip removal
  useEffect(() => {
    const setupMentionChipHandlers = () => {
      if (!finalRef.current) return;
      
      // Function to handle mention chip removal
      const handleMentionRemoveClick = (e) => {
        if (!e.target.classList.contains('mention-remove-icon')) return;
        
        const chipElement = e.target.closest('.mention-chip');
        if (!chipElement) return;
        
        const userId = chipElement.getAttribute('data-user-id');
        
        // Store a reference to the next node to position cursor
        const nextNode = chipElement.nextSibling;
        
        // Remove the chip element
        chipElement.remove();
        
        // Reposition cursor after removing the chip
        if (nextNode) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.setStartBefore(nextNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
        
        // Notify parent component
        if (onMentionRemove && userId) {
          onMentionRemove(userId);
        }
        
        // Update the input value
        onChange(finalRef.current.innerText);
        
        // Prevent event propagation
        e.stopPropagation();
      };
      
      // Add event listener to the input container
      finalRef.current.addEventListener('click', handleMentionRemoveClick);
      
      // Return cleanup function
      return () => {
        if (finalRef.current) {
          finalRef.current.removeEventListener('click', handleMentionRemoveClick);
        }
      };
    };
    
    const cleanup = setupMentionChipHandlers();
    return cleanup;
  }, [onChange, onMentionRemove]);
  
  // Get the text value with mentions formatted properly for saving
  const getTextValueWithMentions = () => {
    if (!finalRef.current) return '';
    
    // Clone the content to avoid modifying the DOM
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = finalRef.current.innerHTML;
    
    // Replace each mention chip with its mention format
    const mentionChips = tempDiv.querySelectorAll('.mention-chip');
    mentionChips.forEach(chip => {
      const mentionFormat = chip.getAttribute('data-mention');
      const textNode = document.createTextNode(mentionFormat);
      chip.parentNode.replaceChild(textNode, chip);
    });
    
    // Convert HTML to text while preserving line breaks and formatting
    let text = tempDiv.innerHTML;
    
    // Replace <br> tags and <div> tags with newlines to preserve formatting
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/div><div[^>]*>/gi, '\n');
    text = text.replace(/<div[^>]*>/gi, '\n');
    text = text.replace(/<\/div>/gi, '');
    
    // Keep formatting tags but ensure they're properly closed
    // Convert strong to b for consistency
    text = text.replace(/<strong>/gi, '<b>');
    text = text.replace(/<\/strong>/gi, '</b>');
    // Convert em to i for consistency
    text = text.replace(/<em>/gi, '<i>');
    text = text.replace(/<\/em>/gi, '</i>');
    
    // Remove any other HTML tags except formatting ones and list tags
    text = text.replace(/<(?!\/?(?:b|i|u|s|ul|ol|li)(?:>|\s.*?>))[^>]*>/g, '');
    
    // Note: We should NOT decode HTML entities here because:
    // 1. This function is called every time we get the value (not just on paste)
    // 2. If the saved content has entities, they should remain as-is
    // 3. Entity decoding should only happen during paste operations
    
    // Clean up any duplicate mentions in the text format
    const mentionPattern = /(@[\w]+)_[\w]+\s+@\1\s+[\w]+/g;
    text = text.replace(mentionPattern, '$1');
    
    return text;
  };
  
  // Fetch user suggestions
  const fetchUsers = useCallback(async (searchText) => {
    if (searchText === lastSearched) return; 
    
    setLastSearched(searchText);
    setLoading(true);
    
    try {
      const searchParams = {
        ...params,
        search: searchText || '',
        limit: 5
      };
      
      const response = await getUsers(searchParams);
      
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          setSuggestions(response.data);
        } else if (response.data.users && Array.isArray(response.data.users)) {
          setSuggestions(response.data.users);
        } else if (response.data.results && Array.isArray(response.data.results)) {
          setSuggestions(response.data.results);
        } else {
          setSuggestions([]);
        }
      } else if (Array.isArray(response)) {
        setSuggestions(response);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [params, lastSearched]);
  
  // Effect to fetch suggestions when a mention is being typed
  useEffect(() => {
    if (currentMention.mentioning && currentMention.text !== lastSearched) {
      fetchUsers(currentMention.text);
    }
  }, [currentMention.mentioning, currentMention.text, fetchUsers, lastSearched]);
  
  // Function to check if we should auto-convert to list
  const checkAndConvertToList = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;
    
    const range = selection.getRangeAt(0);
    const container = range.startContainer;
    
    if (container.nodeType === Node.TEXT_NODE) {
      const text = container.textContent;
      const beforeCursor = text.substring(0, range.startOffset);
      
      // Check for list patterns at the beginning of a line
      const lineStart = beforeCursor.lastIndexOf('\n') + 1;
      const currentLine = beforeCursor.substring(lineStart);
      
      // Pattern for numbered list (e.g., "1. ")
      const numberedMatch = currentLine.match(/^(\d+)\. /);
      if (numberedMatch) {
        // Convert to ordered list
        const textAfterPattern = text.substring(lineStart + numberedMatch[0].length);
        const textBefore = text.substring(0, lineStart);
        
        // Create list structure
        const ol = document.createElement('ol');
        const li = document.createElement('li');
        li.textContent = textAfterPattern || '';
        ol.appendChild(li);
        
        // Replace the text
        const parent = container.parentNode;
        if (textBefore) {
          parent.insertBefore(document.createTextNode(textBefore), container);
        }
        parent.replaceChild(ol, container);
        
        // Position cursor at the end of the list item
        const newRange = document.createRange();
        newRange.selectNodeContents(li);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        return true;
      }
      
      // Pattern for bullet list (e.g., "- ", "* ")
      const bulletMatch = currentLine.match(/^[-*] /);
      if (bulletMatch) {
        // Convert to unordered list
        const textAfterPattern = text.substring(lineStart + bulletMatch[0].length);
        const textBefore = text.substring(0, lineStart);
        
        // Create list structure
        const ul = document.createElement('ul');
        const li = document.createElement('li');
        li.textContent = textAfterPattern || '';
        ul.appendChild(li);
        
        // Replace the text
        const parent = container.parentNode;
        if (textBefore) {
          parent.insertBefore(document.createTextNode(textBefore), container);
        }
        parent.replaceChild(ul, container);
        
        // Position cursor at the end of the list item
        const newRange = document.createRange();
        newRange.selectNodeContents(li);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        return true;
      }
    }
    
    return false;
  };
  
  // Handle input events and detect mentions
  const handleInput = (e) => {
    try {
      // Check if we should auto-convert to a list
      if (checkAndConvertToList()) {
        // List was created, update the value
        const text = getTextValueWithMentions();
        onChange(text);
        return;
      }
      
      // Get the properly formatted text value
      const text = getTextValueWithMentions();
      onChange(text);
      
      // If we just inserted a mention, reset the flag and don't start a new search
      if (justInsertedMention) {
        setJustInsertedMention(false);
        return;
      }
      
      // If cursor is right after a mention chip, don't start searching immediately
      if (isCursorAfterMentionChip()) {
        return;
      }
      
      // Get selection and cursor position
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      const node = range.startContainer;
      const offset = range.startOffset;
      
      // Check if we're typing a mention
      if (node.nodeType === Node.TEXT_NODE) {
        const textBeforeCursor = node.textContent.substring(0, offset);
        const lastAtPos = textBeforeCursor.lastIndexOf('@');
        
        if (lastAtPos !== -1) {
          // Check if @ is at the start or after a space
          if (lastAtPos === 0 || /\s/.test(textBeforeCursor[lastAtPos - 1])) {
            const mentionText = textBeforeCursor.substring(lastAtPos + 1);
            
            // Additional check: don't trigger mention if there's a space in the mention text
            // This indicates we're probably typing regular text after a mention
            if (!mentionText.includes(' ') && mentionText.length <= 50) {
              // Also check if the previous node is a mention chip
              const prevSibling = node.previousSibling;
              
              // If the previous sibling is a mention chip and we're just starting to type, 
              // don't trigger mention search immediately
              if (prevSibling && prevSibling.classList && prevSibling.classList.contains('mention-chip')) {
                // Only allow mention if we explicitly typed @ again
                if (lastAtPos > 0 && textBeforeCursor[lastAtPos - 1] !== ' ') {
                  return; // Don't trigger mention
                }
              }
              
              setCurrentMention({
                mentioning: true,
                startPosition: lastAtPos,
                text: mentionText,
                node: node
              });
              setShowSuggestions(true);
              return;
            }
          }
        }
      }
      
      // If we reach here, we're not typing a mention
      setCurrentMention({
        mentioning: false,
        startPosition: 0,
        text: '',
        node: null
      });
      setShowSuggestions(false);
    } catch (error) {
      console.error('Error handling input:', error);
    }
  };
  
  // Handle paste events to clean up content
  const handlePaste = (e) => {
    e.preventDefault();
    
    // Try to get HTML content first to preserve formatting
    let html = (e.clipboardData || window.clipboardData).getData('text/html');
    let content;
    
    if (html) {
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Clean up the HTML - only keep safe formatting tags and list tags
      const allowedTags = ['b', 'strong', 'i', 'em', 'u', 's', 'br', 'ul', 'ol', 'li'];
      
      // Function to recursively clean nodes
      const cleanNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Decode HTML entities in text nodes
          let text = node.textContent;
          // Handle double-encoded entities first
          text = text.replace(/&amp;nbsp;/g, ' ');
          text = text.replace(/&amp;lt;/g, '<');
          text = text.replace(/&amp;gt;/g, '>');
          text = text.replace(/&amp;quot;/g, '"');
          text = text.replace(/&amp;#39;/g, "'");
          text = text.replace(/&amp;amp;/g, '&');
          // Then single-encoded
          text = text.replace(/&nbsp;/g, ' ');
          text = text.replace(/&lt;/g, '<');
          text = text.replace(/&gt;/g, '>');
          text = text.replace(/&quot;/g, '"');
          text = text.replace(/&#39;/g, "'");
          text = text.replace(/&amp;/g, '&');
          text = text.replace(/\u00A0/g, ' ');
          return document.createTextNode(text);
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          
          // Handle list items specially
          if (tagName === 'li') {
            // Check if this is a numbered list item (from Word/other sources)
            let textContent = node.textContent.trim();
            // Remove common list prefixes that might be in the text
            textContent = textContent.replace(/^\d+\.\s*/, ''); // Remove "1. "
            textContent = textContent.replace(/^[•·-]\s*/, ''); // Remove bullet characters
            
            const li = document.createElement('li');
            // Process child nodes
            Array.from(node.childNodes).forEach(child => {
              if (child.nodeType === Node.TEXT_NODE) {
                // Clean the text content
                let cleanText = child.textContent;
                cleanText = cleanText.replace(/^\d+\.\s*/, '');
                cleanText = cleanText.replace(/^[•·-]\s*/, '');
                li.appendChild(document.createTextNode(cleanText));
              } else {
                li.appendChild(cleanNode(child));
              }
            });
            return li;
          }
          
          // Convert div and p tags to line breaks (unless they contain lists)
          if (tagName === 'div' || tagName === 'p') {
            // Check if this div/p contains a list
            const hasList = Array.from(node.children).some(child => 
              ['ul', 'ol'].includes(child.tagName.toLowerCase())
            );
            
            if (hasList) {
              // Preserve the structure if it contains lists
              const fragment = document.createDocumentFragment();
              Array.from(node.childNodes).forEach(child => {
                fragment.appendChild(cleanNode(child));
              });
              return fragment;
            } else {
              // Regular div/p handling
              const fragment = document.createDocumentFragment();
              if (node.previousSibling) {
                fragment.appendChild(document.createElement('br'));
              }
              Array.from(node.childNodes).forEach(child => {
                fragment.appendChild(cleanNode(child));
              });
              return fragment;
            }
          }
          
          // Keep allowed formatting tags
          if (allowedTags.includes(tagName)) {
            const newElement = document.createElement(tagName);
            Array.from(node.childNodes).forEach(child => {
              newElement.appendChild(cleanNode(child));
            });
            return newElement;
          }
          
          // For other tags, just process their children
          const fragment = document.createDocumentFragment();
          Array.from(node.childNodes).forEach(child => {
            fragment.appendChild(cleanNode(child));
          });
          return fragment;
        }
        
        return document.createTextNode('');
      };
      
      // Clean the content
      const fragment = document.createDocumentFragment();
      Array.from(tempDiv.childNodes).forEach(node => {
        fragment.appendChild(cleanNode(node));
      });
      
      content = fragment;
    } else {
      // Fallback to plain text
      let text = (e.clipboardData || window.clipboardData).getData('text/plain');
      
      // Handle double-encoded entities first
      text = text.replace(/&amp;nbsp;/g, ' ');
      text = text.replace(/&amp;lt;/g, '<');
      text = text.replace(/&amp;gt;/g, '>');
      text = text.replace(/&amp;quot;/g, '"');
      text = text.replace(/&amp;#39;/g, "'");
      text = text.replace(/&amp;amp;/g, '&');
      // Then single-encoded
      text = text.replace(/&nbsp;/g, ' ');
      text = text.replace(/&lt;/g, '<');
      text = text.replace(/&gt;/g, '>');
      text = text.replace(/&quot;/g, '"');
      text = text.replace(/&#39;/g, "'");
      text = text.replace(/&amp;/g, '&');
      text = text.replace(/\u00A0/g, ' ');
      
      // Create fragment with line breaks and detect list patterns
      const lines = text.split('\n');
      const fragment = document.createDocumentFragment();
      let currentList = null;
      let listType = null;
      
      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        
        // Check for numbered list pattern (e.g., "1. ", "2. ")
        const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)/);
        // Check for bullet list pattern (e.g., "- ", "* ", "• ")
        const bulletMatch = trimmedLine.match(/^[•·*-]\s+(.*)/);
        
        if (numberedMatch) {
          // It's a numbered list item
          if (!currentList || listType !== 'ol') {
            // Start a new ordered list
            if (currentList) {
              fragment.appendChild(currentList);
            }
            currentList = document.createElement('ol');
            listType = 'ol';
          }
          const li = document.createElement('li');
          li.textContent = numberedMatch[2] || '';
          currentList.appendChild(li);
        } else if (bulletMatch) {
          // It's a bullet list item
          if (!currentList || listType !== 'ul') {
            // Start a new unordered list
            if (currentList) {
              fragment.appendChild(currentList);
            }
            currentList = document.createElement('ul');
            listType = 'ul';
          }
          const li = document.createElement('li');
          li.textContent = bulletMatch[1] || '';
          currentList.appendChild(li);
        } else {
          // Regular text line
          if (currentList) {
            // End the current list
            fragment.appendChild(currentList);
            currentList = null;
            listType = null;
          }
          
          if (line) {
            fragment.appendChild(document.createTextNode(line));
          }
          if (index < lines.length - 1) {
            fragment.appendChild(document.createElement('br'));
          }
        }
      });
      
      // Add any remaining list
      if (currentList) {
        fragment.appendChild(currentList);
      }
      
      content = fragment;
    }
    
    // Insert the content
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(content);
    
    // Move cursor after the inserted content
    selection.collapseToEnd();
    
    // Trigger input event to update the value
    handleInput();
  };

  // Handle key down events
  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts for lists
    if (e.ctrlKey || e.metaKey) {
      // Ctrl/Cmd + Shift + 7 for numbered list
      if (e.shiftKey && e.key === '7') {
        e.preventDefault();
        document.execCommand('insertOrderedList', false, null);
        handleInput();
        return;
      }
      // Ctrl/Cmd + Shift + 8 for bullet list
      if (e.shiftKey && e.key === '8') {
        e.preventDefault();
        document.execCommand('insertUnorderedList', false, null);
        handleInput();
        return;
      }
    }
    
    if (e.key === 'Escape' && showSuggestions) {
      setShowSuggestions(false);
      e.preventDefault();
      return;
    }
    
    // Handle Tab key in lists
    if (e.key === 'Tab') {
      const selection = window.getSelection();
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        const listItem = range.startContainer.nodeType === Node.TEXT_NODE
          ? range.startContainer.parentElement?.closest('li')
          : range.startContainer.closest?.('li');
        
        if (listItem) {
          e.preventDefault();
          if (e.shiftKey) {
            // Outdent
            document.execCommand('outdent', false, null);
          } else {
            // Indent
            document.execCommand('indent', false, null);
          }
          handleInput();
          return;
        }
      }
    }
    
    // Handle Enter key
    if (e.key === 'Enter') {
      if (showSuggestions) {
        // Don't insert line break when suggestions are shown
        return;
      }
      
      // Check if we're in a list
      const selection = window.getSelection();
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        const listItem = range.startContainer.nodeType === Node.TEXT_NODE
          ? range.startContainer.parentElement?.closest('li')
          : range.startContainer.closest?.('li');
        
        if (listItem && listItem.textContent.trim() === '') {
          // Empty list item - exit the list
          e.preventDefault();
          const list = listItem.parentElement;
          const newP = document.createElement('div');
          const br = document.createElement('br');
          newP.appendChild(br);
          
          // Remove the empty list item
          listItem.remove();
          
          // If list is now empty, remove it
          if (list && list.children.length === 0) {
            list.replaceWith(newP);
          } else {
            // Insert new paragraph after the list
            list.insertAdjacentElement('afterend', newP);
          }
          
          // Move cursor to the new paragraph
          const newRange = document.createRange();
          newRange.setStart(newP, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          handleInput();
          return;
        }
      }
      
      // Check if we're right after a mention chip
      if (selection.rangeCount) {
        const range = selection.getRangeAt(0);
        const container = range.startContainer;
        
        // If we're in an empty text node right after a mention chip, we need to handle Enter specially
        if (container.nodeType === Node.TEXT_NODE && 
            container.textContent === '' && 
            container.previousSibling && 
            container.previousSibling.classList && 
            container.previousSibling.classList.contains('mention-chip')) {
          e.preventDefault();
          
          // Insert a line break
          const br = document.createElement('br');
          range.insertNode(br);
          
          // Create a new text node after the br for cursor positioning
          const newTextNode = document.createTextNode('');
          br.parentNode.insertBefore(newTextNode, br.nextSibling);
          
          // Position cursor in the new text node
          range.setStart(newTextNode, 0);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          
          // Trigger input event
          handleInput();
          return;
        }
      }
      
      // Otherwise let the browser handle line breaks naturally
    }
  };
  
  // Handle selecting a user from suggestions
  const handleSelectUser = (user) => {
    if (!currentMention.mentioning || !finalRef.current) return;
    
    try {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      const currentNode = range.startContainer;
      const currentOffset = range.startOffset;
      
      // Find the @ symbol position in the current text node
      let textNode = currentNode;
      let textContent = '';
      let atPosition = -1;
      
      if (textNode.nodeType === Node.TEXT_NODE) {
        textContent = textNode.textContent;
        // Find the last @ before the current cursor position
        for (let i = currentOffset; i >= 0; i--) {
          if (textContent[i] === '@') {
            atPosition = i;
            break;
          }
        }
      }
      
      if (atPosition === -1) {
        // Fallback: find @ in the entire input
        const fullText = finalRef.current.innerText;
        atPosition = fullText.lastIndexOf('@');
        if (atPosition >= 0) {
          // Create simple text replacement
          const beforeText = fullText.substring(0, atPosition);
          const afterAtPos = fullText.indexOf(' ', atPosition);
          const afterText = afterAtPos >= 0 ? fullText.substring(afterAtPos) : '';
          
          const mentionString = `@${user.firstName}_${user.lastName}`;
          const newText = beforeText + mentionString + ' ' + afterText;
          
          finalRef.current.innerText = newText;
          
          // Position cursor after the mention
          const newPosition = beforeText.length + mentionString.length + 1;
          const newRange = document.createRange();
          const textNode = finalRef.current.firstChild || finalRef.current;
          
          if (textNode.nodeType === Node.TEXT_NODE) {
            newRange.setStart(textNode, Math.min(newPosition, textNode.textContent.length));
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      } else {
        // Replace the @ mention with a chip
        const beforeText = textContent.substring(0, atPosition);
        
        // Find the end of the current mention (space or end of string)
        let endPosition = currentOffset;
        while (endPosition < textContent.length && !/\s/.test(textContent[endPosition])) {
          endPosition++;
        }
        
        const afterText = textContent.substring(endPosition);
        
        // Create the mention chip
        const mentionChip = document.createElement('span');
        mentionChip.className = 'mention-chip';
        mentionChip.contentEditable = 'false';
        mentionChip.setAttribute('data-user-id', user._id);
        mentionChip.setAttribute('data-mention', `@${user.firstName}_${user.lastName}`);
        
        // Create avatar part
        const mentionAvatar = document.createElement('span');
        mentionAvatar.className = 'mention-avatar';
        // Get user initials
        const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
        mentionAvatar.textContent = initials;
        
        // Create text part
        const mentionText = document.createElement('span');
        mentionText.className = 'mention-text';
        mentionText.textContent = `${user.firstName} ${user.lastName}`;
        
        // Create remove button
        const removeBtn = document.createElement('span');
        removeBtn.className = 'mention-remove';
        removeBtn.innerHTML = '<span class="mention-remove-icon">×</span>';
        
        mentionChip.appendChild(mentionAvatar);
        mentionChip.appendChild(mentionText);
        mentionChip.appendChild(removeBtn);
        
        // Create new text nodes
        const beforeNode = document.createTextNode(beforeText);
        // Don't add space if there's already content after (including newlines)
        const afterNode = document.createTextNode(afterText || '');
        
        // Replace the current text node with the new structure
        const parentNode = textNode.parentNode;
        parentNode.insertBefore(beforeNode, textNode);
        parentNode.insertBefore(mentionChip, textNode);
        parentNode.insertBefore(afterNode, textNode);
        parentNode.removeChild(textNode);
        
        // Position cursor right after the mention chip
        const newRange = document.createRange();
        if (afterText) {
          // If there's text after, position at the beginning of it
          newRange.setStart(afterNode, 0);
        } else {
          // If no text after, position after the chip
          newRange.setStartAfter(mentionChip);
        }
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
      
      // Notify parent component
      if (onMentionAdd) {
        onMentionAdd(user);
      }
    } catch (error) {
      console.error('Error inserting mention:', error);
      
      // Simple fallback - just replace the text
      const fullText = finalRef.current.innerText || '';
      const atIndex = fullText.lastIndexOf('@');
      if (atIndex >= 0) {
        const beforeText = fullText.substring(0, atIndex);
        const afterIndex = fullText.indexOf(' ', atIndex);
        const afterText = afterIndex >= 0 ? fullText.substring(afterIndex) : '';
        
        const mentionString = `@${user.firstName}_${user.lastName}`;
        // Ensure proper spacing
        const properAfterText = afterText.startsWith(' ') ? afterText : ' ' + afterText;
        const newText = beforeText + mentionString + properAfterText;
        
        finalRef.current.innerText = newText;
        
        // Try to position cursor after the space
        try {
          const selection = window.getSelection();
          const range = document.createRange();
          const textNode = finalRef.current.firstChild;
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            const pos = beforeText.length + mentionString.length + 1; // +1 for the space
            range.setStart(textNode, Math.min(pos, textNode.textContent.length));
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } catch (cursorError) {
          console.warn('Could not position cursor:', cursorError);
        }
      }
      
      if (onMentionAdd) {
        onMentionAdd(user);
      }
    } finally {
      // Reset mention state
      setCurrentMention({
        mentioning: false,
        startPosition: 0,
        text: '',
        node: null
      });
      setShowSuggestions(false);
      
      // Set flag to prevent immediate mention search reopening
      setJustInsertedMention(true);
      
      // Reset the flag after a short delay to allow normal @ mentions later
      setTimeout(() => {
        setJustInsertedMention(false);
      }, 100);
      
      // Update input value and focus
      onChange(getTextValueWithMentions());
      finalRef.current.focus();
    }
  };
  
  // Formatting toolbar functions
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    finalRef.current?.focus();
    handleInput();
  };
  
  return (
    <MentionInputContainer>
      {/* Formatting Toolbar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5, 
        pb: 1, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        mb: 1
      }}>
        <Tooltip title="Bold (Ctrl+B)">
          <IconButton 
            size="small" 
            onClick={() => applyFormat('bold')}
            sx={{ p: 0.5 }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Italic (Ctrl+I)">
          <IconButton 
            size="small" 
            onClick={() => applyFormat('italic')}
            sx={{ p: 0.5 }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Underline (Ctrl+U)">
          <IconButton 
            size="small" 
            onClick={() => applyFormat('underline')}
            sx={{ p: 0.5 }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Tooltip title="Bullet List (Ctrl+Shift+8)">
          <IconButton 
            size="small" 
            onClick={() => applyFormat('insertUnorderedList')}
            sx={{ p: 0.5 }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Numbered List (Ctrl+Shift+7)">
          <IconButton 
            size="small" 
            onClick={() => applyFormat('insertOrderedList')}
            sx={{ p: 0.5 }}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <EditableDiv
        ref={finalRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        role="textbox"
        aria-multiline="true"
        style={{ ...inputProps.style }}
        data-placeholder={placeholder}
      />
      
      {/* Mention suggestions dropdown */}
      <Popper
        open={showSuggestions && currentMention.mentioning}
        anchorEl={finalRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: finalRef.current?.offsetWidth || 300 }}
      >
        <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
          <Paper elevation={3}>
            <List sx={{ width: '100%', maxHeight: 250, overflow: 'auto', p: 0 }}>
              {loading && (
                <ListItem>
                  <ListItemText primary="Loading users..." />
                </ListItem>
              )}
              
              {!loading && Array.isArray(suggestions) && suggestions.length > 0 && 
                suggestions.map((user) => (
                  <ListItem 
                    key={user._id || `user-${Math.random()}`}
                    disablePadding
                    onClick={() => handleSelectUser(user)}
                  >
                    <ListItemButton>
                      <ListItemAvatar>
                        <SecureAvatar
                          src={user.photo}
                          alt={`${user.firstName} ${user.lastName}`}
                        >
                          {user.firstName?.[0] || 'U'}
                        </SecureAvatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${user.firstName || ''} ${user.lastName || ''}`}
                        secondary={user.email || user.role?.name || ''}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              }
              
              {!loading && (!Array.isArray(suggestions) || suggestions.length === 0) && (
                <ListItem>
                  <ListItemText primary="No users found" />
                </ListItem>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </MentionInputContainer>
  );
});

export default InlineMentionInput;